import React, { useState, useEffect } from 'react';
import { Download, AlertCircle, FileSpreadsheet, Loader } from 'lucide-react';
import apiService from '../services/api';
import logger from '../utils/logger';

interface SpreadsheetPreviewProps {
  attachmentId: number;
  filename: string;
  downloadUrl: string;
}

interface SpreadsheetData {
  success: boolean;
  filename: string;
  headers: string[];
  rows: any[][];
  total_rows: number;
  truncated: boolean;
  sheet_names: string[] | null;
}

const SpreadsheetPreview: React.FC<SpreadsheetPreviewProps> = ({
  attachmentId,
  filename,
  downloadUrl,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SpreadsheetData | null>(null);

  useEffect(() => {
    const loadPreview = async () => {
      try {
        setLoading(true);
        setError(null);
        logger.debug('[SpreadsheetPreview] Loading preview for attachment');

        const result = await apiService.previewSpreadsheet(attachmentId);

        logger.debug(
          `[SpreadsheetPreview] Loaded ${result.total_rows} rows with ${result.headers?.length || 0} columns`
        );
        setData(result);
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to load preview';
        logger.error('[SpreadsheetPreview] Error loading preview:', err);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
  }, [attachmentId]);

  // Loading state
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center bg-white">
        <Loader className="animate-spin mr-2 text-green-600" size={24} />
        <span className="text-neutral-600">Loading preview...</span>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center mb-3">
            <AlertCircle className="text-red-600 mr-2" size={32} />
            <div>
              <h3 className="text-lg font-semibold text-red-800">Preview Failed</h3>
              <p className="text-sm text-red-700">
                {error || 'Failed to load preview'}
              </p>
            </div>
          </div>
          <a
            href={downloadUrl}
            className="mt-3 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            download
          >
            <Download size={16} className="mr-2" />
            Download File
          </a>
        </div>
      </div>
    );
  }

  // Success state - render table
  return (
    <div className="flex flex-col h-full">
      {/* Header with filename and download button */}
      <div className="flex items-center justify-between bg-green-600 text-white p-3 rounded-t-lg shadow-sm">
        <div className="flex items-center">
          <FileSpreadsheet size={20} className="mr-2" />
          <span className="font-medium">{data.filename}</span>
          <span className="ml-3 text-sm opacity-90">
            {data.total_rows} row{data.total_rows !== 1 ? 's' : ''}
            {data.truncated && ' (showing first 100)'}
          </span>
          {data.sheet_names && data.sheet_names.length > 1 && (
            <span className="ml-3 text-sm opacity-90">
              • {data.sheet_names.length} sheets
            </span>
          )}
        </div>
        <a
          href={downloadUrl}
          className="flex items-center px-3 py-1 bg-white text-green-600 rounded hover:bg-green-50 transition-colors font-medium"
          title="Download file"
          download
        >
          <Download size={16} className="mr-1" />
          Download
        </a>
      </div>

      {/* Table container with scrolling */}
      <div className="flex-1 overflow-auto border-l border-r border-b border-neutral-200 rounded-b-lg bg-white">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-neutral-100 sticky top-0 z-10">
            <tr>
              {data.headers.map((header, i) => (
                <th
                  key={i}
                  className="px-3 py-2 text-left font-semibold text-neutral-700 border-b-2 border-r border-neutral-300 whitespace-nowrap"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={`
                  ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}
                  hover:bg-blue-50 transition-colors
                `}
              >
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="px-3 py-2 border-b border-r border-neutral-200"
                  >
                    {cell === null || cell === undefined ? (
                      <span className="text-transparent select-none">-</span>
                    ) : (
                      <span className="whitespace-nowrap">{String(cell)}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Truncation warning */}
      {data.truncated && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 flex items-start">
          <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Preview Limited:</strong> Showing first 100 rows only. Download the
            file to view all data.
          </div>
        </div>
      )}
    </div>
  );
};

export default SpreadsheetPreview;
